import type { Document } from "../document/Document";
import type {
  AdjustmentLayer,
  ArtboardLayer,
  BackgroundLayer,
  FrameLayer,
  GradientFillLayer,
  GroupLayer,
  Layer,
  LinkedSmartObjectLayer,
  PatternFillLayer,
  RasterLayer,
  ShapeLayer,
  SmartObjectLayer,
  SolidFillLayer,
  VideoFrameLayer,
  ThreeDLayer,
} from "../document/Layer";
import type { Command } from "./Command";
import { DeleteLayerCommand } from "./DeleteLayerCommand";

// Deep-clone an existing layer (pixel buffers copied via PixelBuffer.clone())
// and insert the clone directly above the source, in the same parent.
// Invert removes the clone.

export class DuplicateLayerCommand implements Command {
  readonly kind = "duplicate-layer";
  constructor(
    private readonly sourceLayerId: string,
    private readonly newId: string,
  ) {}

  apply(doc: Document): Document {
    const source = findLayer(doc.layers, this.sourceLayerId);
    if (!source) return doc;
    const clone = cloneLayer(source, this.newId);
    return { ...doc, layers: insertAbove(doc.layers, this.sourceLayerId, clone) };
  }

  invert(_before: Document, _after: Document): Command {
    return new DeleteLayerCommand(this.newId);
  }
}

function findLayer(layers: readonly Layer[], id: string): Layer | null {
  for (const l of layers) {
    if (l.id === id) return l;
    if (l.kind === "group") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
    if (l.kind === "artboard") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function cloneLayer(layer: Layer, newId: string): Layer {
  const nameCopy = `${layer.name} copy`;
  if (layer.kind === "raster") {
    const clone: RasterLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      pixels: layer.pixels.clone(),
    };
    return clone;
  }
  if (layer.kind === "type") {
    return { ...layer, id: newId, name: nameCopy };
  }
  if (layer.kind === "adjustment") {
    const clone: AdjustmentLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
    };
    return clone;
  }
  if (layer.kind === "smart-object") {
    const so = layer as SmartObjectLayer;
    const clone: SmartObjectLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      embedded: cloneEmbeddedDocument(so.embedded, newId),
    };
    return clone;
  }
  if (layer.kind === "shape") {
    const s = layer as ShapeLayer;
    const clone: ShapeLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      path: JSON.parse(JSON.stringify(s.path)),
      vectorMask: s.vectorMask ? JSON.parse(JSON.stringify(s.vectorMask)) : null,
    };
    return clone;
  }
  if (layer.kind === "solid-fill") {
    const clone: SolidFillLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
    };
    return clone;
  }
  if (layer.kind === "gradient-fill") {
    const clone: GradientFillLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      stops: JSON.parse(JSON.stringify(layer.stops)),
    };
    return clone;
  }
  if (layer.kind === "pattern-fill") {
    const clone: PatternFillLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      pattern: layer.pattern
        ? { pixels: new Uint8ClampedArray(layer.pattern.pixels), width: layer.pattern.width, height: layer.pattern.height }
        : null,
    };
    return clone;
  }
  if (layer.kind === "background") {
    const clone: BackgroundLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      pixels: layer.pixels.clone(),
    };
    return clone;
  }
  if (layer.kind === "artboard") {
    const ab = layer as ArtboardLayer;
    const clone: ArtboardLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      children: ab.children.map((c, i) => cloneLayer(c, `${newId}/${i}`)),
    };
    return clone;
  }
  if (layer.kind === "frame") {
    const fr = layer as FrameLayer;
    const clone: FrameLayer = {
      ...layer,
      id: newId,
      name: nameCopy,
      content: fr.content ? cloneLayer(fr.content, `${newId}/content`) : null,
    };
    return clone;
  }
  if (layer.kind === "linked-smart-object") {
    const lso = layer as LinkedSmartObjectLayer;
    const cloneBase: Omit<LinkedSmartObjectLayer, "composite"> = {
      ...layer,
      id: newId,
      name: nameCopy,
    };
    const clone = {
      ...cloneBase,
      ...(lso.composite ? { composite: lso.composite.clone() } : {}),
    } as LinkedSmartObjectLayer;
    return clone;
  }
  if (layer.kind === "video-frame") {
    const vf = layer as VideoFrameLayer;
    const cloneBase: Omit<VideoFrameLayer, "composite"> = {
      ...vf,
      id: newId,
      name: nameCopy,
    };
    const clone = {
      ...cloneBase,
      ...(vf.composite ? { composite: vf.composite.clone() } : {}),
    } as VideoFrameLayer;
    return clone;
  }
  if (layer.kind === "3d") {
    const td = layer as ThreeDLayer;
    const cloneBase: Omit<ThreeDLayer, "composite"> = {
      ...td,
      id: newId,
      name: nameCopy,
    };
    const clone = {
      ...cloneBase,
      ...(td.composite ? { composite: td.composite.clone() } : {}),
    } as ThreeDLayer;
    return clone;
  }
  const clone: GroupLayer = {
    ...layer,
    id: newId,
    name: nameCopy,
    children: (layer as GroupLayer).children.map((c: Layer, i: number) => cloneLayer(c, `${newId}/${i}`)),
  };
  return clone;
}

function cloneEmbeddedDocument(doc: Document, parentId: string): Document {
  return {
    ...doc,
    layers: doc.layers.map((l, i) => cloneLayer(l, `${parentId}/embedded/${i}`)),
    activeLayerId: doc.activeLayerId ? `${parentId}/embedded/${doc.layers.findIndex(l => l.id === doc.activeLayerId)}` : null,
  };
}

function insertAbove(layers: readonly Layer[], targetId: string, layer: Layer): Layer[] {
  const out: Layer[] = [];
  let inserted = false;
  for (const l of layers) {
    if (!inserted) {
      if (l.kind === "group") {
        const before = l.children;
        const after = insertAbove(before, targetId, layer);
        if (after !== before) {
          out.push({ ...l, children: after });
          inserted = true;
          continue;
        }
      }
      if (l.kind === "artboard") {
        const before = l.children;
        const after = insertAbove(before, targetId, layer);
        if (after !== before) {
          out.push({ ...l, children: after });
          inserted = true;
          continue;
        }
      }
    }
    out.push(l);
    if (l.id === targetId && !inserted) {
      out.push(layer);
      inserted = true;
    }
  }
  return out;
}
